/**
 * SkillChip — Tag preset for skill / language chips (not a parallel styling system).
 *
 * Tone is intentional and ≠ Tag intent:
 *   - 'primary' (default): purple subtle — classifier / skill lists
 *   - 'info': light-blue fill (Meta CV / talent pool); not Tag info=blue-soft
 *
 * Geometry follows Tag (`sm` | `md` | `lg` | `xl`; md = Figma 24px default).
 */

import React from 'react';
import { Tag } from '../Tag';
import type { TagSize } from '../Tag/Tag.types';
import { cn } from '@/utils/cn';

export type SkillChipTone = 'primary' | 'info';

export interface SkillChipProps {
  /** Skill display label */
  label: string;
  /** Edit mode: show remove (×) button */
  closable?: boolean;
  /** Called when × is clicked in edit mode */
  onRemove?: (e: React.MouseEvent) => void;
  /** Tag size variant */
  size?: TagSize;
  /** Color tone: 'primary' (purple, default) or 'info' (blue, meta-resume) */
  tone?: SkillChipTone;
  /** Disabled state */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
  title?: string;
  'aria-label'?: string;
}

export const SkillChip: React.FC<SkillChipProps> = ({
  label,
  closable = false,
  onRemove,
  size = 'md',
  tone = 'primary',
  disabled = false,
  className,
  title,
  'aria-label': ariaLabel,
}) => {
  // tone=info: Meta CV / talent-pool blue uses light-blue (not Tag info subtle = blue-soft).
  // Override Tag's primary-subtle fill; ! needed because cn() does not tailwind-merge custom tokens.
  const toneClass =
    tone === 'info' ? '!bg-light-blue !text-dark-invirtus' : undefined;

  return (
    <Tag
      intent="primary"
      variant="subtle"
      pill
      size={size}
      closable={closable}
      onClose={onRemove}
      disabled={disabled}
      wrap
      className={cn(toneClass, className)}
      title={title}
      aria-label={ariaLabel}
    >
      {label}
    </Tag>
  );
};

SkillChip.displayName = 'SkillChip';
